import React, { useEffect, useState } from "react";
import InputComponent from "../UniversalComponents/InputComponent";
import Button from "../UniversalComponents/DefaultButton";
import { getUserToken } from "../../utils/utilityFunctions/localServices";
import { useGetContactQuery, usePostContactMutation } from "../../utils/customer/customerTaskTable";
import { pushAlert } from "../../utils/alertSlice";
import { hideLoader, showLoader } from "../../utils/loaderSlice";
import { useDispatch } from "react-redux";
import BackArrowCompoentIcon from "../../assets/images/icons/BackArrowCompoentIcon";
import { ReactComponent as CircleEdit } from "../../assets/images/icons/CircleEdit.svg";
import { ReactComponent as CircleDelete } from "../../assets/images/icons/CircleDelete.svg";
import { ReactComponent as AddField } from "../../assets/images/icons/AddField.svg";
import { ReactComponent as MessageIcon } from "../../assets/images/icons/MsgIcon.svg";
import { ReactComponent as CallIcon } from "../../assets/images/icons/CallIcon.svg";
import CopyIconComponent from "../UniversalComponents/CopyIconComponent";
import { useLocation, useNavigate } from "react-router-dom";

const ContactForm = ({
  vendorUUID,
  editData,
  handleBackButtonClick,
  vendorName,
  refetchVendorHeader,
  refetchVendorValues,
}) => {
  const dispatch = useDispatch();
  const userToken = getUserToken();
  const location = useLocation();
  const navigate = useNavigate();
  const VendorId = location?.state?.vendorData?.id || null;
  const [contacts, setContacts] = useState([]);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", id: "", is_primary: 0 });
  const [showAddForm, setShowAddForm] = useState(location.state?.isShowMode ?? false);
  const [selectedEditIndex, setSelectedEditIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isDisable, setIsDisable] = useState("");

  const [postContact, { isLoading, isSuccess, isError, data: response }] = usePostContactMutation();
  const {
    data: getData,
    isSuccess: isFetched,
    refetch,
  } = useGetContactQuery(
    { UUID: editData?.row_id || VendorId, token: userToken },
    { skip: !editData?.row_id && !VendorId }
  );

  useEffect(() => {
    if (isFetched && getData?.data?.length > 0) {
      setContacts(getData.data);
      if (location.state?.activeTab === "contact" && location.state?.isShowMode) {
        // Find the primary contact or use the first one
        const primaryIndex = getData.data.findIndex((c) => c.is_primary === 1);
        const index = primaryIndex !== -1 ? primaryIndex : 0;
        setFormData({
          name: getData.data[index]?.name || "",
          phone: getData.data[index]?.phone || "",
          email: getData.data[index]?.email || "",
          id: getData.data[index]?.id || "",
          is_primary: getData.data[index]?.is_primary || 0,
        });
        setSelectedEditIndex(index);
        setIsDisable(index.toString());
        setShowAddForm(true);
      } else {
        setShowAddForm(false);
        setFormData({ name: "", phone: "", email: "", id: "", is_primary: 0 });
        setSelectedEditIndex(null);
        setIsDisable("");
      }
    } else if (location.state?.activeTab === "contact" && location.state?.isShowMode) {
      // If no data, open blank form
      setShowAddForm(true);
      setFormData({ name: "", phone: "", email: "", id: "", is_primary: 0 });
      setSelectedEditIndex(null);
      setIsDisable("");
    } else {
      setContacts([]);
      setShowAddForm(false);
      setFormData({ name: "", phone: "", email: "", id: "", is_primary: 0 });
      setSelectedEditIndex(null);
      setIsDisable("");
    }
  }, [isFetched, getData, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^(?!.*\.\.)[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSaveAndSubmit = () => {
    const { name, email } = formData;

    if (!name || name.trim() === "") {
      dispatch(
        pushAlert({
          message: "Please enter name",
          type: "danger",
          visible: true,
        })
      );
      return;
    }

    if (email && !isValidEmail(email)) {
      dispatch(
        pushAlert({
          message: "Please enter a valid email address",
          type: "danger",
          visible: true,
        })
      );
      return;
    }

    let updated;
    if (selectedEditIndex !== null) {
      updated = [...contacts];
      updated[selectedEditIndex] = { ...formData, id: contacts[selectedEditIndex]?.id };
    } else {
      updated = [...contacts, formData];
    }
    setContacts(updated);
    resetForm();
    setShowAddForm(false);
    setTimeout(() => submitContacts(updated), 0);
  };

  const resetForm = () => {
    setFormData({ name: "", phone: "", email: "", id: "", is_primary: 0 });
    setSelectedEditIndex(null);
  };

  const submitContacts = async (list) => {
    const payload = {
      contacts: list.map((c) => ({
        name: c.name,
        phone: c.phone,
        email: c.email,
        ...(c.id && { id: c.id }),
        is_primary: c.is_primary || 0,
      })),
    };
    try {
      await postContact({ UUID: editData?.row_id || vendorUUID || VendorId, token: userToken, body: payload }).unwrap();
      if (editData?.row_id) {
        refetch();
        setIsDisable("");
      }
    } catch (err) {
      console.error("Failed to submit contact details", err);
    }
  };

  const handleDefaultChange = async (index) => {
    const updated = contacts.map((item, i) => ({
      ...item,
      is_primary: i === index ? 1 : 0,
    }));
    setContacts(updated);
    await submitContacts(updated);
  };

  const handleEdit = (index) => {
    setFormData({
      name: contacts[index].name || "",
      phone: contacts[index].phone || "",
      email: contacts[index].email || "",
      id: contacts[index].id || "",
      is_primary: contacts[index].is_primary || 0,
    });
    setSelectedEditIndex(index);
    setShowAddForm(true);
    setIsDisable(index.toString());
  };

  const handleDelete = (index) => {
    const updated = [...contacts];
    updated.splice(index, 1);
    setContacts(updated);
    resetForm();
    setShowAddForm(false);
  };

  const handleCancelForm = () => {
    resetForm();
    setShowAddForm(false);
    setIsDisable("");
  };

  useEffect(() => {
    if (isLoading) dispatch(showLoader());
    else dispatch(hideLoader());

    if (isSuccess) {
      dispatch(pushAlert({ message: response?.message || "Saved", type: "success", visible: true }));
      refetchVendorHeader();
      refetchVendorValues();
      setIsDisable("");
    }
    if (isError) dispatch(pushAlert({ message: response?.message || "Error", type: "danger", visible: true }));
  }, [isLoading, isSuccess, isError]);

  return (
    <div className="h-full flex flex-col pl-4">
      <div className="flex flex-col w-full min-h-0 grow">
        <div className="flex justify-between items-center border-[#D2DAE1] px-2 py-2">
          <div className="flex items-center ">
            <button
              onClick={() => {
                if (location.pathname === "/add-vendor") {
                  navigate("/vendors");
                } else {
                  navigate(`/vendor-profile/${location?.state?.vendorData?.id}`, {
                    state: { isEditMode: true },
                  });
                }
              }}
            >
              <BackArrowCompoentIcon />
            </button>
            <h3 className="text-16 font-600 text-[#383838] mx-2 items-center">
              <span className="items-center text-[rgba(56,56,56,0.6)] text-16 font-500">Vendor &gt; </span>
              {vendorName}
            </h3>
          </div>
          {!showAddForm && (
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}
              className="group cursor-pointer border border-[#D2DAE1] hover:border-[#004EFF] rounded-md flex items-center gap-2 px-3 py-1.5 "
            >
              <AddField className="w-4 h-4 fill-[#666] group-hover:fill-[#004EFF]" />
              <span className="text-base font-normal text-[#666] group-hover:text-[#004EFF]">Add New</span>
            </button>
          )}
        </div>

        {(showAddForm || contacts?.length === 0) && (
          <div className="mb-6 border rounded p-4">
            <div className="grid grid-cols-2 gap-x-40 gap-y-4 ">
              <InputComponent
                label={
                  <span className="flex">
                    Name <span className="text-red-500 flex items-end ml-1">*</span>
                  </span>
                }
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter Name"
                height="30px"
                className="flex items-baseline "
                labelClassName="flex items-center text-[#666] w-1/2"
                rowView
              />
              <InputComponent
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter Email"
                height="30px"
                className="flex items-baseline"
                labelClassName="flex items-center text-[#666] w-1/2"
                type="email"
                error={formData.email && !isValidEmail(formData.email)}
                helperText={formData.email && !isValidEmail(formData.email) ? "Please write a valid Email" : ""}
              />
              <InputComponent
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter Phone"
                height="30px"
                type="text"
                className="flex items-baseline"
                labelClassName="flex items-center text-[#666] w-1/2"
              />
            </div>
            <div className="flex justify-start gap-3 mt-8">
              <Button
                variant="secondary"
                onClick={handleCancelForm}
                className="whitespace-nowrap px-4 bg-[white] border-solid hover:bg-[white] hover:border-[#004EFF] hover:text-[#004EFF]"
              >
                CANCEL
              </Button>
              <Button className="bg-[#5489FF] hover:bg-[#004EFF]" onClick={handleSaveAndSubmit}>
                {selectedEditIndex !== null ? "UPDATE" : "SAVE"}
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2 overflow-y-auto thinscrollbar h-full mb-4 ">
          {contacts?.length > 0 && (
            <div className="border border-[#D2DAE1] rounded-md p-3">
              {contacts
                .slice()
                .sort((a, b) => b.is_primary - a.is_primary)
                .map((item, index) => {
                  const isLastItem = index === contacts?.length - 1;
                  return (
                    <div
                      className={`w-full ${
                        isDisable !== null && isDisable === index.toString()
                          ? "opacity-50 pointer-events-none bg-[#D2DAE1]"
                          : ""
                      }`}
                      key={index}
                    >
                      <div
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        className={`relative ${isLastItem ? "" : "border-b-2"} p-2 transition-all duration-200 items-center w-full ${
                          hoveredIndex === index ? " bg-[#F4F4F4] rounded-md" : "border-[#D2DAE1]"
                        }`}
                      >
                        <div className="gap-y-4 space-y-4 flex justify-between items-center">
                          <div className="text-16 font-400 text-[#383838] whitespace-normal break-all">{item.name}</div>
                          <label
                            className={`text-sm ${item.is_primary === 1 ? "text-[#2999BC] font-medium" : "text-[#666666]"}`}
                          >
                            <input
                              type="checkbox"
                              checked={item.is_primary === 1}
                              onChange={() => handleDefaultChange(index)}
                              className={`mr-2 h-4 w-4 rounded bg-white focus:ring-0 focus:outline-none ${
                                item.is_primary === 1
                                  ? "border-[#2999BC] text-[#2999BC]"
                                  : "border-gray-400 text-gray-500"
                              }`}
                            />
                            Default Contact
                          </label>
                        </div>
                        <div className="gap-y-4 space-y-4 grid grid-cols-3 items-center">
                          <div className="text-14 font-400 text-[#6B7280] flex gap-1 items-center mt-4 justify-start whitespace-normal break-all">
                            <MessageIcon /> {item?.email} {item?.email && <CopyIconComponent copyValue={item?.email} />}
                          </div>
                          <div className="text-14 font-400 text-[#6B7280] flex gap-1 items-center justify-start ps-36 whitespace-normal break-all">
                            <CallIcon /> {item?.phone} {item?.phone && <CopyIconComponent copyValue={item?.phone} />}
                          </div>
                          <div
                            className={`flex gap-2 items-center transition-opacity duration-300 justify-end ${
                              hoveredIndex === index ? "opacity-100" : "opacity-0"
                            }`}
                          >
                            <button onClick={() => handleEdit(index)}>
                              <CircleEdit />
                            </button>
                            {false && (
                              <button onClick={() => handleDelete(index)}>
                                <CircleDelete />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
